import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Download, CheckCircle, AlertCircle, X, Loader2, FileText } from 'lucide-react';
import { notify } from '../stores/notificationStore';
import { importAPI } from '../utils/api';
import { logger } from '../utils/logger';
import { Button } from './ui-v2';

interface CSVImportProps {
  entityType: 'produits' | 'clients';
  onImportComplete?: () => void;
  className?: string;
}

interface ImportResult {
  success: number;
  total: number;
  errors: Array<{
    line: number;
    data: any;
    error: string;
  }>;
  categoriesCreated?: string[];
}

export const CSVImport: React.FC<CSVImportProps> = ({ entityType, onImportComplete, className = '' }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        notify.error(t('common.errors.csvOnly'), t('common.error'));
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      // Télécharger le template via l'API
      const blob = entityType === 'clients'
        ? await importAPI.getClientTemplate()
        : await importAPI.getProductTemplate();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${entityType}_template.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      logger.error('Erreur téléchargement template:', error);
      notify.error(t('common.errors.templateDownloadFailed'), t('common.error'));
    }
  };

  const handleUpload = async () => {
    if (!file) {
      notify.error(t('common.csvImport.clickToSelectCsv'), t('common.error'));
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      // ✅ Utilisation de la nouvelle API avec validation + import
      const response = entityType === 'clients'
        ? await importAPI.importClients(file)
        : await importAPI.importProducts(file);

      if (response.success) {
        const data = response.data;
        setResult({
          success: data.successCount,
          total: data.totalRows,
          errors: data.errors.map((err: any) => ({
            line: err.row,
            data: err.field || '',
            error: err.message,
          })),
        });

        if (data.failureCount === 0) {
          notify.success(t('common.csvImport.importedCount', { success: data.successCount, entity: entityType, total: data.totalRows }), t('common.success'));
        } else {
          notify.warning(
            t('common.csvImport.importedCount', { success: data.successCount, entity: entityType, total: data.totalRows }) + ' - ' + t('common.csvImport.errorsCount', { count: data.failureCount }),
            t('common.csvImport.importResult')
          );
        }

        // Réinitialiser le fichier
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // Callback
        if (onImportComplete) {
          onImportComplete();
        }
      }
    } catch (error: any) {
      logger.error('Erreur import CSV:', error);
      notify.error(error.message || t('common.errors.importFailed'), t('common.error'));
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setFile(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      {/* Bouton d'ouverture */}
      <Button
        onClick={() => setIsOpen(true)}
        variant="secondary"
        icon={<Upload className="w-4 h-4" />}
        className={className}
      >
        {t('common.csvImport.importCsv')}
      </Button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <Upload className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-900">
                  {entityType === 'produits' ? t('common.csvImport.importProducts') : t('common.csvImport.importClients')}
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">{t('common.csvImport.howItWorks')}</h3>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>{t('common.csvImport.step1')}</li>
                  <li>{t('common.csvImport.step2')}</li>
                  <li>{t('common.csvImport.step3')}</li>
                  <li>{t('common.csvImport.step4')}</li>
                </ol>
              </div>

              {/* Télécharger template */}
              <div>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  {t('common.csvImport.downloadCsvTemplate')}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  {t('common.csvImport.templateContainsExamples')}
                </p>
              </div>

              {/* Upload zone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.csvImport.fileToImport')}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                    id="csv-file-input"
                  />
                  <label
                    htmlFor="csv-file-input"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <FileText className="w-12 h-12 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {file ? (
                        <span className="font-medium text-blue-600">{file.name}</span>
                      ) : (
                        <>{t('common.csvImport.clickToSelectCsv')}</>
                      )}
                    </span>
                    <span className="text-xs text-gray-500">{t('common.csvImport.acceptedFormat')}</span>
                  </label>
                </div>
              </div>

              {/* Bouton d'import */}
              {file && (
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-teal-600 to-purple-600 hover:from-teal-700 hover:to-purple-700 shadow-lg shadow-teal-500/30 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('common.csvImport.startImport')}...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      {t('common.csvImport.startImport')}
                    </>
                  )}
                </button>
              )}

              {/* Résultats */}
              {result && (
                <div className="space-y-4">
                  {/* Résumé */}
                  <div className={`border rounded-lg p-4 ${
                    result.errors.length === 0
                      ? 'bg-green-50 border-green-200'
                      : 'bg-orange-50 border-orange-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      {result.errors.length === 0 ? (
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <h3 className={`font-semibold mb-1 ${
                          result.errors.length === 0 ? 'text-green-900' : 'text-orange-900'
                        }`}>
                          {t('common.csvImport.importResult')}
                        </h3>
                        <p className={`text-sm ${
                          result.errors.length === 0 ? 'text-green-800' : 'text-orange-800'
                        }`}>
                          {t('common.csvImport.importedCount', { success: result.success, entity: entityType, total: result.total })}
                          {result.errors.length > 0 && (
                            <> · {t('common.csvImport.errorsCount', { count: result.errors.length })}</>
                          )}
                        </p>
                        {entityType === 'produits' && result.categoriesCreated && result.categoriesCreated.length > 0 && (
                          <p className="text-sm text-green-700 mt-2">
                            {t('common.csvImport.categoriesCreated', { count: result.categoriesCreated.length, categories: result.categoriesCreated.join(', ') })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Liste des erreurs */}
                  {result.errors.length > 0 && (
                    <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                      <h4 className="font-semibold text-red-900 mb-3">{t('common.csvImport.errorDetails')}</h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {result.errors.map((error, index) => (
                          <div key={index} className="bg-white border border-red-200 rounded p-3">
                            <p className="text-sm font-medium text-red-900">
                              {t('common.csvImport.lineErrorDetail', { line: error.line, error: error.error })}
                            </p>
                            <p className="text-xs text-red-700 mt-1">
                              {t('common.csvImport.dataLabel', { data: JSON.stringify(error.data) })}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
